import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import moment from "moment";
import "moment/locale/vi";

import {
    getNotifications,
    getUnreadNotificationsCount,
    markAllNotificationsRead,
    markNotificationRead,
} from "../../slices/thunks";
import { resolveNotificationRedirect } from "../../helpers/notificationRedirect";
import { DEFAULT_NOTIFICATION_ICON, NOTIFICATION_TYPE_ICONS } from "../../constants/notificationIcons";
import { AppDispatch } from "../../store";
import NotificationDetailOffcanvas from "../../Components/Common/NotificationDetailOffcanvas";
import HeaderSidebarToggle from "../../Components/Common/HeaderSidebarToggle";
import HomeLogoLink from "../../Components/Common/HomeLogoLink";

moment.locale("vi");

const DEFAULT_PAGE_SIZE = 10;

const parseBooleanLike = (value: any): boolean | undefined => {
    if (typeof value === "boolean") {
        return value;
    }

    if (typeof value === "number") {
        if (value === 1) {
            return true;
        }

        if (value === 0) {
            return false;
        }
    }

    if (typeof value === "string") {
        const normalized = value.trim().toLowerCase();
        if (["true", "1", "yes", "y", "on", "read", "seen", "viewed", "đã đọc"].includes(normalized)) {
            return true;
        }

        if (["false", "0", "no", "n", "off", "unread", "chưa đọc"].includes(normalized)) {
            return false;
        }
    }

    return undefined;
};

const getNotificationId = (notification: any): string | undefined => {
    if (!notification || typeof notification !== "object") {
        return undefined;
    }

    const candidates = [
        notification.id,
        notification.notificationId,
        notification.notification_id,
        notification.uuid,
        notification._id,
        notification.key,
        notification.code,
    ];

    for (const candidate of candidates) {
        if (candidate !== undefined && candidate !== null) {
            return String(candidate);
        }
    }

    return undefined;
};

const getNotificationMessage = (notification: any): string => {
    if (!notification || typeof notification !== "object") {
        return "Thông báo";
    }

    const candidates = [
        notification.message,
        notification.body,
        notification.content,
        notification.description,
        notification.detail,
        notification.text,
    ];

    for (const candidate of candidates) {
        if (typeof candidate === "string" && candidate.trim().length > 0) {
            return candidate;
        }
    }

    return "Thông báo";
};

const getNotificationTitle = (notification: any): string => {
    if (!notification || typeof notification !== "object") {
        return "Thông báo";
    }

    const candidates = [
        notification.title,
        notification.subject,
        notification.name,
    ];

    for (const candidate of candidates) {
        if (typeof candidate === "string" && candidate.trim().length > 0) {
            return candidate;
        }
    }

    return "Thông báo";
};

const getNotificationIconClass = (notification: any): string => {
    if (!notification || typeof notification !== "object") {
        return DEFAULT_NOTIFICATION_ICON;
    }

    const typeCandidate = typeof notification.type === "string" ? notification.type.trim().toLowerCase() : "";
    if (typeCandidate && NOTIFICATION_TYPE_ICONS[typeCandidate]) {
        return NOTIFICATION_TYPE_ICONS[typeCandidate];
    }

    return DEFAULT_NOTIFICATION_ICON;
};

const getNotificationTimestamp = (notification: any): any => {
    if (!notification || typeof notification !== "object") {
        return null;
    }

    const candidates = [
        notification.createdAt,
        notification.created_at,
        notification.timestamp,
        notification.time,
        notification.sentAt,
        notification.sent_at,
        notification.date,
        notification.updatedAt,
        notification.updated_at,
    ];

    for (const candidate of candidates) {
        if (candidate !== undefined && candidate !== null) {
            return candidate;
        }
    }

    return null;
};

const formatNotificationTime = (value: any): string | null => {
    if (!value) {
        return null;
    }

    if (value instanceof Date) {
        const date = moment(value);
        if (date.isValid()) {
            return date.fromNow();
        }
    }

    const dateFromValue = moment(value);
    if (dateFromValue.isValid()) {
        return dateFromValue.fromNow();
    }

    const numericValue = Number(value);
    if (!Number.isNaN(numericValue)) {
        const dateFromNumber = moment(numericValue);
        if (dateFromNumber.isValid()) {
            return dateFromNumber.fromNow();
        }
    }

    return String(value);
};

const isNotificationRead = (notification: any): boolean => {
    if (!notification || typeof notification !== "object") {
        return false;
    }

    const booleanCandidates = [
        notification.read,
        notification.isRead,
        notification.seen,
        notification.viewed,
        notification.status,
        notification.is_read,
    ];

    for (const candidate of booleanCandidates) {
        const parsed = parseBooleanLike(candidate);
        if (parsed !== undefined) {
            return parsed;
        }
    }

    const timestampCandidates = [
        notification.readAt,
        notification.read_at,
        notification.seenAt,
        notification.seen_at,
    ];

    if (timestampCandidates.some((candidate) => candidate)) {
        return true;
    }

    return false;
};

const isNotificationNew = (notification: any): boolean => {
    if (isNotificationRead(notification)) {
        return false;
    }

    const timestamp = getNotificationTimestamp(notification);
    if (!timestamp) {
        return false;
    }

    const createdAt = moment(timestamp);
    if (!createdAt.isValid()) {
        return false;
    }

    const now = moment();
    return now.diff(createdAt, "hours", true) <= 1;
};

const isSystemNotification = (notification: any): boolean => {
    if (!notification || typeof notification !== "object") {
        return false;
    }

    return typeof notification.type === "string" && notification.type.toLowerCase() === "system";
};

const isOrderNotification = (notification: any): boolean => {
    if (!notification || typeof notification !== "object") {
        return false;
    }

    return typeof notification.type === "string" && notification.type.toLowerCase().includes("order");
};

const firstNumber = (...values: any[]): number | undefined => {
    for (const value of values) {
        const numericValue = Number(value);
        if (Number.isFinite(numericValue)) {
            return numericValue;
        }
    }

    return undefined;
};

const firstBoolean = (...values: any[]): boolean | undefined => {
    for (const value of values) {
        if (typeof value === "boolean") {
            return value;
        }

        const parsed = parseBooleanLike(value);
        if (parsed !== undefined) {
            return parsed;
        }
    }

    return undefined;
};

const extractNotificationItems = (notificationsState: any): any[] => {
    const candidates = [
        notificationsState?.items,
        notificationsState?.notifications,
        notificationsState?.data,
        notificationsState?.data?.data,
        notificationsState?.data?.data?.data,
    ];

    for (const candidate of candidates) {
        if (Array.isArray(candidate)) {
            return candidate;
        }
    }

    return [];
};

const isObjectLike = (value: any): boolean => {
    return Boolean(value) && typeof value === "object" && !Array.isArray(value);
};

const isPaginationLike = (value: any): boolean => {
    if (!isObjectLike(value)) {
        return false;
    }

    return [
        "page",
        "currentPage",
        "current_page",
        "pageSize",
        "perPage",
        "per_page",
        "total",
        "totalPages",
        "lastPage",
        "last_page",
        "hasNext",
        "has_next",
        "hasMore",
        "has_more",
    ].some((key) => value[key] !== undefined && value[key] !== null);
};

const extractNotificationMeta = (notificationsState: any): any => {
    const candidates = [
        notificationsState?.meta,
        notificationsState?.pagination,

        // Trường hợp reducer lưu raw Laravel response:
        // state.notifications.data = {
        //   success: true,
        //   data: {
        //     data: [...],
        //     current_page: 1,
        //     last_page: 6,
        //     per_page: 10,
        //     total: 59
        //   }
        // }
        notificationsState?.data?.data,

        // Các shape khác có thể gặp:
        notificationsState?.data?.meta,
        notificationsState?.data?.pagination,
        notificationsState?.data,
        notificationsState,
    ];

    for (const candidate of candidates) {
        if (isPaginationLike(candidate)) {
            return candidate;
        }
    }

    return {};
};

const NotificationPage = () => {
    const dispatch = useDispatch<AppDispatch>();
    const navigate = useNavigate();

    const notificationsState = useSelector((state: any) => state.notifications ?? {});

    const items = useMemo(() => {
        return extractNotificationItems(notificationsState);
    }, [notificationsState]);

    const meta = useMemo(() => {
        return extractNotificationMeta(notificationsState);
    }, [notificationsState]);

    const loading = useMemo(() => {
        const loadingState = notificationsState.loading;
        if (loadingState && typeof loadingState === "object") {
            return loadingState;
        }
        return {};
    }, [notificationsState.loading]);

    const error = typeof notificationsState.error === "string" ? notificationsState.error : null;

    const [page, setPage] = useState<number>(1);
    const [pageSize, setPageSize] = useState<number>(DEFAULT_PAGE_SIZE);
    const [selectedNotification, setSelectedNotification] = useState<any | null>(null);
    const [isLoadingMore, setIsLoadingMore] = useState(false);

    const requestedPageRef = useRef<number>(0);

    const markReadState: Record<string, boolean> = useMemo(() => {
        const state = loading.markRead;
        if (state && typeof state === "object") {
            return state;
        }
        return {};
    }, [loading.markRead]);

    const isLoadingList = Boolean(loading.list);
    const isMarkingAllRead = Boolean(loading.markAllRead);

    const derivedMeta = useMemo(() => {
        const currentPage =
            firstNumber(
                meta?.page,
                meta?.currentPage,
                meta?.current_page,
                page
            ) ?? page;

        const size =
            firstNumber(
                meta?.pageSize,
                meta?.perPage,
                meta?.per_page,
                meta?.limit,
                pageSize
            ) ?? pageSize;

        const total =
            firstNumber(
                meta?.total,
                meta?.total_count,
                meta?.count
            ) ?? items.length;

        const totalPages =
            firstNumber(
                meta?.totalPages,
                meta?.lastPage,
                meta?.last_page
            ) ?? (size > 0 ? Math.max(1, Math.ceil(total / size)) : 1);

        const explicitHasNext = firstBoolean(
            meta?.hasNext,
            meta?.has_next,
            meta?.hasMore,
            meta?.has_more
        );

        const explicitHasPrevious = firstBoolean(
            meta?.hasPrevious,
            meta?.has_previous
        );

        return {
            currentPage,
            pageSize: size,
            total,
            totalPages,
            hasNext: explicitHasNext !== undefined ? explicitHasNext : currentPage < totalPages,
            hasPrevious: explicitHasPrevious !== undefined ? explicitHasPrevious : currentPage > 1,
        };
    }, [items.length, meta, page, pageSize]);

    const { currentPage, hasNext } = derivedMeta;

    const isLoadingListRef = useRef(isLoadingList);
    const isLoadingMoreRef = useRef(isLoadingMore);
    const hasNextRef = useRef(hasNext);
    const currentPageRef = useRef(currentPage);

    useEffect(() => {
        isLoadingListRef.current = isLoadingList;
    }, [isLoadingList]);

    useEffect(() => {
        isLoadingMoreRef.current = isLoadingMore;
    }, [isLoadingMore]);

    useEffect(() => {
        hasNextRef.current = hasNext;
    }, [hasNext]);

    useEffect(() => {
        currentPageRef.current = currentPage;
    }, [currentPage]);

    useEffect(() => {
        const metaPageSize = firstNumber(meta?.pageSize, meta?.perPage, meta?.per_page);
        if (metaPageSize && metaPageSize > 0 && metaPageSize !== pageSize) {
            setPageSize(metaPageSize);
        }
    }, [meta?.pageSize, meta?.perPage, meta?.per_page, pageSize]);

    const refreshNotifications = useCallback(
        (targetPage: number, options?: { append?: boolean }) => {
            const params = {
                page: targetPage,
                pageSize,
                perPage: pageSize,
                per_page: pageSize,
                limit: pageSize,
                append: options?.append ?? targetPage > 1,
            };

            requestedPageRef.current = targetPage;
            dispatch(getNotifications(params));
        },
        [dispatch, pageSize]
    );

    useEffect(() => {
        refreshNotifications(page, { append: page > 1 });
    }, [refreshNotifications, page]);

    useEffect(() => {
        if (!isLoadingList) {
            setIsLoadingMore(false);
            isLoadingMoreRef.current = false;
        }
    }, [isLoadingList]);

    const handleLoadMore = useCallback(() => {
        if (isLoadingListRef.current || isLoadingMoreRef.current || !hasNextRef.current) {
            return;
        }

        const nextPage = currentPageRef.current + 1;

        // Tránh gọi trùng cùng một page nếu IntersectionObserver bắn nhiều lần.
        if (requestedPageRef.current >= nextPage) {
            return;
        }

        isLoadingMoreRef.current = true;
        setIsLoadingMore(true);
        setPage((prevPage) => Math.max(prevPage + 1, nextPage));
    }, []);

    const handleMarkAllAsRead = useCallback(() => {
        if (isMarkingAllRead || items.length === 0) {
            return;
        }

        dispatch(markAllNotificationsRead())
            .unwrap()
            .then(() => {
                dispatch(getUnreadNotificationsCount());
                setPage(1);
                requestedPageRef.current = 0;
                refreshNotifications(1, { append: false });
            })
            .catch(() => {
                // Ignore errors here; the error state is handled via Redux
            });
    }, [dispatch, isMarkingAllRead, items.length, refreshNotifications]);

    const handleNotificationClick = useCallback(
        (notification: any) => {
            const id = getNotificationId(notification);
            const read = isNotificationRead(notification);
            const isMarking = id ? Boolean(markReadState[id]) : false;

            if (id && !read && !isMarking) {
                dispatch(markNotificationRead({ notificationId: id }))
                    .unwrap()
                    .then(() => {
                        dispatch(getUnreadNotificationsCount());
                    })
                    .catch(() => {
                        // Ignore errors here; the error state is handled via Redux
                    });
            }

            if (isSystemNotification(notification)) {
                setSelectedNotification(notification);
                return;
            }

            if (isOrderNotification(notification)) {
                navigate("/order", { state: { notification } });
                return;
            }

            const redirect = resolveNotificationRedirect(notification);
            if (!redirect.target) {
                return;
            }

            if (redirect.isExternal) {
                window.location.href = redirect.target;
                return;
            }

            navigate(redirect.target, { state: { notification } });
        },
        [dispatch, markReadState, navigate]
    );

    const showLoadingPlaceholder = isLoadingList && items.length === 0;
    const showEmptyState = !isLoadingList && items.length === 0;

    return (
        <React.Fragment>
            <div className="header-area" id="headerArea">
                <div className="container h-100 d-flex text-center align-items-center justify-content-between rtl-flex-d-row-r">
                    <HomeLogoLink className="me-2" />

                    <div className="page-heading">
                        <h6 className="mb-0">Thông báo</h6>
                    </div>

                    <HeaderSidebarToggle className="ms-2" />
                </div>
            </div>

            <div className="page-content-wrapper">
                <div className="container">
                    <div className="section-heading d-flex align-items-center pt-3 justify-content-between rtl-flex-d-row-r">
                        <h6 className="mb-0">Danh sách thông báo</h6>
                        <div className="d-flex align-items-center gap-2">
                            {isLoadingList && (
                                <span className="spinner-border spinner-border-sm text-primary" role="status">
                                    <span className="visually-hidden">Đang tải...</span>
                                </span>
                            )}
                            <button
                                type="button"
                                className="notification-mark-all-read text-secondary btn btn-link p-0 border-0"
                                onClick={handleMarkAllAsRead}
                                disabled={isMarkingAllRead || items.length === 0}
                            >
                                {isMarkingAllRead && (
                                    <span className="spinner-border spinner-border-sm align-middle me-2" role="status">
                                        <span className="visually-hidden">Đang xử lý...</span>
                                    </span>
                                )}
                                Đọc hết
                            </button>
                        </div>
                    </div>

                    {error && (
                        <div className="alert alert-danger" role="alert">
                            {error}
                        </div>
                    )}

                    <div className="notification-area pb-2">
                        {showLoadingPlaceholder && (
                            <div className="text-center py-5">
                                <div className="spinner-border text-primary" role="status">
                                    <span className="visually-hidden">Đang tải...</span>
                                </div>
                                <div className="text-muted mt-2">Đang tải thông báo...</div>
                            </div>
                        )}

                        {!showLoadingPlaceholder && showEmptyState && (
                            <div className="text-center py-5 text-muted">
                                <i className="mdi mdi-bell-off-outline d-block fs-1 mb-3"></i>
                                <p className="mb-0">Không có thông báo nào.</p>
                            </div>
                        )}

                        {!showLoadingPlaceholder && items.length > 0 && (
                            <div className="list-group">
                                {items.map((notification: any, index: number) => {
                                    const id = getNotificationId(notification) ?? `notification-${index}`;
                                    const read = isNotificationRead(notification);
                                    const isNew = isNotificationNew(notification);
                                    const iconClass = getNotificationIconClass(notification);
                                    const message = isSystemNotification(notification)
                                        ? getNotificationTitle(notification)
                                        : getNotificationMessage(notification);
                                    const timeLabel = formatNotificationTime(getNotificationTimestamp(notification));
                                    const isMarking = id ? Boolean(markReadState[id]) : false;

                                    return (
                                        <button
                                            type="button"
                                            className={`list-group-item list-group-item-action d-flex align-items-center border-0${read ? " readed" : ""}`}
                                            key={id}
                                            onClick={() => handleNotificationClick(notification)}
                                            disabled={isMarking}
                                        >
                                            <span className="noti-icon position-relative">
                                                <i className={iconClass}></i>
                                                {isMarking && (
                                                    <span className="spinner-border spinner-border-sm text-primary position-absolute top-50 start-50 translate-middle" role="status">
                                                        <span className="visually-hidden">Đang cập nhật...</span>
                                                    </span>
                                                )}
                                            </span>

                                            <div className="noti-info text-start">
                                                <p className="mb-1 d-flex align-items-center gap-2">
                                                    <span>{message}</span>
                                                    {isNew && (
                                                        <span className="badge bg-primary-subtle text-primary">Mới</span>
                                                    )}
                                                </p>
                                                {timeLabel && (
                                                    <span className="text-muted small">{timeLabel}</span>
                                                )}
                                            </div>
                                        </button>
                                    );
                                })}
                            </div>
                        )}

                        {!showLoadingPlaceholder && items.length > 0 && (
                            <div className="text-center py-3">
                                {hasNext ? (
                                    <button
                                        type="button"
                                        className="btn btn-link text-primary text-decoration-none p-0"
                                        onClick={handleLoadMore}
                                        disabled={isLoadingList || isLoadingMore}
                                    >
                                        {(isLoadingList || isLoadingMore) ? (
                                            <React.Fragment>
                                                <span className="spinner-border spinner-border-sm align-middle me-2" role="status">
                                                    <span className="visually-hidden">Đang tải...</span>
                                                </span>
                                                Đang tải thêm...
                                            </React.Fragment>
                                        ) : (
                                            "Xem thông báo cũ"
                                        )}
                                    </button>
                                ) : (
                                    <span className="text-muted small">Đã tải hết thông báo</span>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            <NotificationDetailOffcanvas
                isOpen={Boolean(selectedNotification)}
                notification={selectedNotification}
                onClose={() => setSelectedNotification(null)}
            />
        </React.Fragment>
    );
};

export default NotificationPage;
